// =====================================================
// Stripe Plan Configuration (Client-Safe)
// =====================================================

/**
 * Subscription plan configuration
 * This file is safe to import in client components
 * (does not initialize Stripe API client)
 */
export const SUBSCRIPTION_PLANS = {
  basic: {
    offered: true as const,
    id: 'basic' as const,
    name: 'Starter',
    displayName: 'Starter',
    tagline: 'For Individuals',
    ctaText: 'Start Free Trial',
    price: 39.99,
    currency: 'eur' as const,
    interval: 'month' as const,
    stripePriceId: process.env.NEXT_PUBLIC_STRIPE_BASIC_PRICE_ID,
    trialDays: 14,
    // R3 landing reconciliation (2026-07-11, r3-judge-E-verdict.md Decision 2,
    // Alex-approved masterplan §0 addition): this list previously undersold
    // Starter — the dashboard/KPIs, Cosmos Alerts, and connected organizations
    // below were Pro-only here while the founder confirmed in the binding
    // transcript that Starter already ships them. Nothing is newly locked;
    // this reconciles copy (and the `analytics` limit flag below) to product
    // reality. Blast radius: read-only consumers only (RegisterForm/
    // PlanSelectionUI/PlanUpgradeDialog/subscription-expired render this array
    // verbatim; subscription-guard.ts's FEATURE_TIERS gate is a SEPARATE,
    // independently-maintained enforcement table this edit does not touch).
    features: [
      '500 leads/month',
      '3 team members',
      'Leads from Meta, TikTok, forms, website and email',
      'Kanban pipeline (customizable stages)',
      'Contacts and clients, with full history',
      'CSV import/export',
      'Tasks, calendar and meetings',
      'Team and internal chat',
      'Offers & service catalog',
      'Connected organizations (lead sharing)',
      'Analytics dashboard',
      'Cosmos AI Assistant',
      'COSMOS Alerts (AI anomaly detection)',
      'AI WhatsApp Bot (add-on)',
    ],
    // Support is universal across offered plans (transcript-confirmed, R3):
    // never a Pro-only differentiator. Matches pro.support below verbatim.
    support: 'Email & WhatsApp support',
    // priceIncludesVat: R3 judge-E-verdict.md Decision 7 derivation contract —
    // the landing's "TVA inclus"/"VAT included" label reads this boolean via
    // plan-facts.ts's formatVatLabel(); it is never hand-typed in content.ts
    // or JSX. `as const` so plan-facts.ts's formatVatLabel(boolean) narrows.
    priceIncludesVat: true as const,
    limits: {
      leads: 500,
      users: 3,
      messages: 0,
      aiAutomation: false,
      // Reconciled true (was false) — see the features-array comment above.
      analytics: true,
    },
  },
  pro: {
    offered: true as const,
    id: 'pro' as const,
    name: 'Pro',
    displayName: 'Pro',
    tagline: 'For Teams',
    ctaText: 'Start Free Trial',
    price: 79.99,
    currency: 'eur' as const,
    interval: 'month' as const,
    stripePriceId: process.env.NEXT_PUBLIC_STRIPE_PRO_PRICE_ID,
    trialDays: 14,
    // R3 reconciliation (see basic.features comment above): the only real
    // Pro differentiator left is time tracking/attendance plus the higher
    // leads/users limits below — analytics, Cosmos Alerts, and connected
    // organizations are shared with Starter now, so they are no longer
    // repeated here as if exclusive.
    features: [
      'Everything in Starter',
      'Unlimited leads',
      '15 team members',
      'Time tracking & attendance',
      'AI WhatsApp Bot (add-on)',
    ],
    // Same value as basic.support — support is universal, not a tier benefit.
    support: 'Email & WhatsApp support',
    priceIncludesVat: true as const,
    popular: true,
    limits: {
      leads: Infinity,
      users: 15,
      messages: 0,
      aiAutomation: false,
      analytics: true,
    },
  },
  // Legacy plan — no new signups; existing 2 subscriptions still active. Hidden from pricing UI via `offered: false`.
  enterprise: {
    offered: false as const,
    id: 'enterprise' as const,
    name: 'Enterprise',
    displayName: 'Enterprise',
    tagline: 'Built for Scale',
    ctaText: 'Contact Sales',
    price: 499.99,
    currency: 'eur' as const,
    interval: 'month' as const,
    stripePriceId: process.env.NEXT_PUBLIC_STRIPE_ENTERPRISE_PRICE_ID,
    trialDays: 0,
    features: [
      'Everything in Pro',
      'Unlimited leads',
      'Unlimited team members',
      'Advanced analytics',
      'Custom integrations',
      'SLA guarantee',
      'AI WhatsApp Bot included',
    ],
    support: 'Dedicated account manager',
    includedModules: ['ai_lead_onboarding'] as const,
    limits: {
      leads: Infinity,
      users: Infinity,
      messages: 0,
      aiAutomation: false,
      analytics: true,
    },
  },
} as const;

export type PlanId = keyof typeof SUBSCRIPTION_PLANS;

export const OFFERED_PLAN_IDS = (Object.keys(SUBSCRIPTION_PLANS) as PlanId[]).filter(
  (id) => SUBSCRIPTION_PLANS[id].offered
) as ReadonlyArray<PlanId>;

/**
 * Module catalog with flat pricing + usage-based conversation quotas
 *
 * Pricing model:
 * - Basic & Pro: €50/month flat (200 conversations included)
 * - Enterprise: FREE (included in plan, unlimited conversations)
 * - Overage: €5 per 50 extra conversations
 *
 * CRITICAL: Never trust prices from client - always fetch from here or Stripe
 */
export const MODULE_CATALOG = {
  ai_lead_onboarding: {
    id: 'ai_lead_onboarding',
    name: 'Business AI Assistant',
    stripePriceId: process.env.NEXT_PUBLIC_STRIPE_PRICE_AI_LEAD_ONBOARDING,
    trialDays: 7, // 7-day free trial (one-time per org, first activation only)
    // Flat pricing for Basic & Pro, free for Enterprise
    pricing: {
      basic: { price: 50, discount: 0, label: '€50/month' },
      pro: { price: 50, discount: 0, label: '€50/month' },
      enterprise: { price: 0, discount: 100, label: 'Included', included: true },
    },
    price: 50, // Base price for display
    priceInCents: 5000,
    currency: 'eur',
    interval: 'month' as const,
    // Conversation quota (1 conversation = 1 WhatsApp AI session)
    quota: {
      includedConversations: 200,
      overagePrice: 5, // €5 per overage block
      overageBlockSize: 50, // 50 conversations per overage block
    },
    description:
      'Your AI-powered WhatsApp assistant — qualifies leads, sends offers, confirms orders, and manages conversations automatically.',
    longDescription:
      'Connect your WhatsApp Business account and let AI handle your customer conversations. From qualifying new leads to sending personalized offers and confirming orders — the Business AI Assistant adapts to whatever your business needs. Powered by Google Gemini for natural, human-like responses.',
    features: [
      '200 AI conversations included per month',
      'Automated lead qualification & scoring',
      'Smart order confirmations & follow-ups',
      'Personalized offer delivery via WhatsApp',
      'Natural conversations powered by Google Gemini',
      'Fully customizable message flows & templates',
      '€5 per 50 extra conversations after quota (only replies count)',
    ],
    category: 'automation' as const,
    // URL to navigate to when module is purchased
    moduleUrl: '/ai/settings',
  },
  // Future modules:
  // advanced_analytics: { ... },
  // crm_integrations: { ... },
} as const;

export type ModuleId = keyof typeof MODULE_CATALOG;

/**
 * Canonical id of the WhatsApp/AI module ("Business AI Assistant").
 *
 * Exported so entitlement gates never hand-type the string. Every AI affordance
 * (Start AI, AI card fields, AI filters/sort/export columns) and every AI send
 * path gates on THIS constant, so a rename can never leave a surface ungated.
 * Client-safe — this file initializes no Stripe API client.
 */
export const AI_MODULE_ID = MODULE_CATALOG.ai_lead_onboarding.id;

/** Module pricing for a specific plan tier */
interface ModulePlanPricing {
  price: number;
  discount: number;
  label: string;
  included?: boolean;
}

/**
 * Get module pricing for a specific subscription plan
 */
export function getModulePriceForPlan(moduleId: ModuleId, planId: PlanId): ModulePlanPricing {
  const module = MODULE_CATALOG[moduleId];
  if (!module || !('pricing' in module)) {
    return { price: (module as any)?.price || 0, discount: 0, label: 'Unknown', included: false };
  }

  const pricing = (module.pricing as Record<PlanId, ModulePlanPricing>)[planId];
  return (
    pricing || {
      price: module.price,
      discount: 0,
      label: `€${module.price}/month`, // @format-helper-currency-glyph-ok reason=CMG-platform-module-pricing-is-EUR
      included: false,
    }
  );
}

/**
 * Check if a module is included free with a plan
 */
export function isModuleIncludedInPlan(moduleId: ModuleId, planId: PlanId): boolean {
  const pricing = getModulePriceForPlan(moduleId, planId);
  return pricing.included === true;
}

/**
 * Get conversation quota for a module (if usage-based)
 */
export function getModuleQuota(
  moduleId: ModuleId
): { includedConversations: number; overagePrice: number; overageBlockSize: number } | null {
  const module = MODULE_CATALOG[moduleId];
  return 'quota' in module ? module.quota : null;
}

/**
 * Format price for display. The list prices above are EUR; a Romanian buyer is charged
 * in lei (charge-currency.ts), so a caller showing that buyer's price passes the lei
 * amount with currency 'ron' -> "207.67 lei/month". Unknown codes print the ISO code
 * rather than a wrong glyph (this used to render every non-EUR currency as "$").
 */
export function formatPrice(price: number, interval: string, currency: string = 'eur'): string {
  if (price === 0) return 'Free';
  const per = interval === 'forever' ? 'forever' : interval;
  const code = currency.toLowerCase();
  if (code === 'eur') return `€${price}/${per}`; // @format-helper-currency-glyph-ok reason=CMG-platform-list-price-is-EUR
  if (code === 'usd') return `$${price}/${per}`;
  if (code === 'ron') return `${price.toFixed(2)} lei/${per}`;
  return `${price} ${code.toUpperCase()}/${per}`;
}
